import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { alert, Page, Button, Modal, Checkbox, Input, Tabs } from 'equasion-lib';
import { FiChevronLeft, FiSearch } from 'react-icons/fi';
import ZoneSelectWithScan from '@/lib/shared-coms/ZoneSelectWithScan';
import PurchaseOrderList from './com/PurchaseOrderList';
import {
  receiveValidation,
  stockValidation
} from './UnserializedPurchaseOrders.validation';
import './UnserializedPurchaseOrders.scss';

const TAB_FROM_URL = { receiving: 'receiving', stocking: 'received' };
const TAB_TO_URL = { receiving: 'receiving', received: 'stocking' };

const UnserializedPurchaseOrders = ({
  purchaseOrders,
  bulkProductStatusUpdate,
  unserializedBulkProductCreate,
  clientId,
  refetch
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const selectedTab = useMemo(() => {
    const mapped = tabFromUrl ? TAB_FROM_URL[tabFromUrl] : undefined;
    return mapped ?? 'receiving';
  }, [tabFromUrl]);

  const [selectedPurchaseOrderId, setSelectedPurchaseOrderId] = useState(null);
  const [selectedOrderItem, setSelectedOrderItem] = useState(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState({ field: 'number', direction: 'asc' });

  const handleSort = (field) => {
    setSortBy((prev) => {
      if (prev.field === field) {
        return { ...prev, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { field, direction: 'asc' };
    });
  };

  const sortPurchaseOrders = (orders) => {
    const { field, direction } = sortBy;
    const multiplier = direction === 'asc' ? 1 : -1;
    return [...orders].sort((a, b) => {
      const aVal =
        field === 'number' ? a.clientPurchaseOrderNumber : a.vendor?.companyName ?? '';
      const bVal =
        field === 'number' ? b.clientPurchaseOrderNumber : b.vendor?.companyName ?? '';
      return multiplier * aVal.localeCompare(bVal, undefined, { numeric: true });
    });
  };
  const navigate = useNavigate();

  const handleTabChange = (value) => {
    const urlTab = TAB_TO_URL[value];
    setSearchParams(urlTab ? { tab: urlTab } : {});
  };

  const {
    id: selectedPurchaseOrderItemId,
    quantity: selectedOrderItemQuantity,
    received: isStocking,
    productPackagingKind: { id: productPackagingKindId } = {}
  } = selectedOrderItem || {};

  const submitUnserializedBulkProductCreate = async (values, actions) => {
    const { setSubmitting } = actions;
    try {
      const { productNotMissing, missingQuantity } = values;
      await unserializedBulkProductCreate({
        variables: {
          purchaseOrderItemId: selectedPurchaseOrderItemId,
          missingCount: productNotMissing ? undefined : parseInt(missingQuantity)
        }
      });
      alert('Received', 'Successfully received products', 4, 'success');
      setSelectedPurchaseOrderId(null);
      setSelectedOrderItem(null);
      refetch();
    } catch (e) {
      console.log(e);
    }
    setSubmitting(false);
  };

  const submitBulkProductUpdate = async (values, actions) => {
    const { setSubmitting } = actions;
    try {
      const { productNotMissing, missingQuantity, locationId } = values;
      await bulkProductStatusUpdate({
        variables: {
          missingCount: productNotMissing ? undefined : parseInt(missingQuantity),
          limit: selectedOrderItemQuantity,
          fromProductAttributes: {
            productPackagingKindId,
            purchaseOrderId: selectedPurchaseOrderId,
            status: 14
          },
          toProductAttributes: {
            status: 15,
            locationId: locationId
          }
        }
      });
      alert('Stocked', 'Successfully stocked products', 4, 'success');
      setSelectedPurchaseOrderId(null);
      setSelectedOrderItem(null);
      refetch();
    } catch (e) {
      console.log(`ERROR UPDATING PRODUCTS: ${e}`);
    }
    setSubmitting(false);
  };

  return (
    <Page className="otc-purchase-orders action-page">
      <div className="action-top-bar">
        <Button
          type="secondary"
          iconBefore={<FiChevronLeft />}
          onClick={() => navigate('/')}
        >
          Back
        </Button>
        <div className="order-details">
          <h1>Inbound OTC/UPC</h1>
          <h2>{purchaseOrders.length} Orders</h2>
        </div>
      </div>
      <div className="action-page-body">
        <div className="top-filters">
          <Tabs
            menuOnly
            selectedKey={selectedTab}
            onTabChange={handleTabChange}
            data={[
              {
                label: 'Receiving',
                key: 'receiving'
              },
              {
                label: 'Stocking',
                key: 'received'
              }
            ]}
          />
          <Input
            form={false}
            label="Search"
            iconAfter={<FiSearch />}
            value={search}
            onChange={({ value }) => setSearch(value)}
          />
        </div>
        <PurchaseOrderList
          purchaseOrders={sortPurchaseOrders(
            purchaseOrders
              .filter(({ clientStatus }) => clientStatus === selectedTab)
              .filter(({ clientPurchaseOrderNumber, vendor }) => {
                const companyName = vendor?.companyName ?? '';
                return (
                  clientPurchaseOrderNumber
                    .toLowerCase()
                    .includes(search.toLowerCase()) ||
                  companyName.toLowerCase().includes(search.toLowerCase())
                );
              })
          )}
          selectedStatus={selectedTab}
          sortBy={sortBy}
          onSort={handleSort}
        />
      </div>

      <Modal
        className="otc-purchase-order-modal"
        modalOpen={!!selectedOrderItem}
        setModalOpen={() => setSelectedOrderItem(null)}
      >
        {!!isStocking ? (
          <Formik
            initialValues={{
              locationId: '',
              productNotMissing: true,
              missingQuantity: 1
            }}
            validationSchema={stockValidation(selectedOrderItemQuantity)}
            validateOnBlur={false}
            onSubmit={submitBulkProductUpdate}
          >
            {({ isSubmitting, values }) => {
              const { productNotMissing, locationId } = values;
              console.log('locationId', locationId);
              return (
                <Form className="otc-form">
                  <Checkbox name="productNotMissing" label="No products are missing" />
                  {!productNotMissing && (
                    <Input
                      inputType="number"
                      name="missingQuantity"
                      label="Missing Quantity"
                    />
                  )}
                  Location:
                  <ZoneSelectWithScan
                    name="locationId"
                    label="Location"
                    companyId={clientId}
                    scanTitle="Scan the stocking zone"
                    scanHint="Scan the label on the zone these items are being put away in."
                  />
                  <Button type="primary" fullWidth isSubmit disabled={isSubmitting}>
                    {productNotMissing ? 'Stock' : 'Split & Stock'}
                  </Button>
                </Form>
              );
            }}
          </Formik>
        ) : (
          <Formik
            initialValues={{}}
            validationSchema={receiveValidation(selectedOrderItemQuantity)}
            validateOnBlur={false}
            onSubmit={submitUnserializedBulkProductCreate}
          >
            {({ isSubmitting }) => {
              return (
                <Form className="otc-form">
                  <Button type="primary" fullWidth isSubmit disabled={isSubmitting}>
                    Receive
                  </Button>
                </Form>
              );
            }}
          </Formik>
        )}
      </Modal>
    </Page>
  );
};

export default UnserializedPurchaseOrders;
